import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2025-10-01 23:48:11 +0200 (Wed, 01 Oct 2025) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 156871 $
// Purpose     : debug parts
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Menu, Input, Item } from 'semantic-ui-react';
// ---Framework imports
import { sum, has, reversed, isString } from 'ldash';
import { LoremBuilder, XMenu, Toolbar } from 'uix/components';
import { SearchableJSONTree } from 'uix/chunks';
import { funcPart, loadLazy as ll } from 'uix/parts';
import log from 'uix/logging';
import { Actions } from 'uix/registries';
import { Parts } from 'uix/registry';
import { TracebackDiv } from 'uix/components/error';
import { useSelector, useState } from 'uix/hooks';
// ---Local imports
// ---Exports
export const SysTool = ll(()=>import(/* webpackChunkName: 'debug' */ './systools'));
export const DebugFooter = ll(()=>import(/* webpackChunkName: 'debug' */ './footer'));
export const Whiteboard = ll(()=>import(/* webpackChunkName: 'whiteboard' */ './whiteboard'));
export { TestErrorDuringRender } from './errorParts';
export { default as SimpleInterpreter } from '../loadInterpreter';
const PPrintTree = ({ data, autoExpanded, fullExpand, searchString })=>{
    let tracebackDiv = null;
    if (has(data, 'props') && has(data.props, 'error') && has(data.props, 'traceback')) {
        // format and replace traceback information
        data = cloneDeep(data);
        let { traceback, error, errorMsg, errorInfo } = data.props;
        tracebackDiv = TracebackDiv(traceback, error, errorMsg, errorInfo);
        data.props.traceback = 'REPLACE_ME';
        delete data.props.errorInfo;
        delete data.props.errorMsg;
        delete data.props.error;
    }
    let expand = (keyPath, data, level)=>{
        if (fullExpand || level < 2) return true;
        let dotted = reversed(keyPath).join('.');
        if (autoExpanded === level || isString(autoExpanded) && autoExpanded.startsWith(dotted)) return true;
        return level < 2;
    };
    let valueRenderer = (raw, value, ...keyPath)=>{
        let dotted = reversed(keyPath).join('.');
        let result = raw;
        if (keyPath[0] == 'traceback') {
            result = tracebackDiv;
        }
    };
    let theme = {
        tree: ({ style, className })=>{
            style = {
                ...style,
                margin: 0
            };
            return {
                style,
                className
            };
        },
        nestedNodeLabel: ({ style }, keyPath, nodeType, expanded)=>({
                style: {
                    ...style,
                    color: isString(autoExpanded) && autoExpanded.startsWith(reversed(keyPath).join('.')) ? 'white' : style.color
                }
            })
    };
    return /*#__PURE__*/ _jsx("div", {
        style: {
            flex: 1,
            overflow: 'auto',
            backgroundColor: 'rgb(0, 43, 54)'
        },
        children: /*#__PURE__*/ _jsx(SearchableJSONTree, {
            hideRoot: true,
            query: searchString,
            data: data,
            shouldExpandNode: expand,
            theme: theme,
            style: {
                margin: 0
            }
        })
    });
};
export const PPrintViz = funcPart({
    partStyle: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        //~ minWidth: '400px',
        overflow: 'hidden',
        fontSize: '0.9rem',
        lineHeight: '1.0rem'
    }
}, ({})=>{
    let pprintData = useSelector((root)=>root.meta.pprint);
    let [fullExpand, setFullExpand] = useState(false);
    let [searchString, setSearchString] = useState('');
    let { __expanded__ = 1, ...data } = pprintData;
    let download = (data, exportName = 'pprint_out')=>{
        // https://stackoverflow.com/a/30800715
        let dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(data));
        let a = document.createElement('a');
        a.setAttribute('href', dataStr);
        a.setAttribute('download', exportName + '.json');
        a.click();
        a.remove();
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Toolbar, {
                attached: true,
                children: [
                    /*#__PURE__*/ _jsx(XMenu.Item, {
                        icon: "close",
                        onClick: ()=>vl.pprint({
                                __CLEAR__: true
                            })
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Toggle, {
                        icon: "expand",
                        title: "expand all levels (instead of first)",
                        value: fullExpand,
                        set: setFullExpand
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Item, {
                        icon: "download",
                        onClick: ()=>download(data),
                        title: "download as JSON"
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Input, {
                        icon: "search",
                        placeholder: "Search...",
                        value: searchString || '',
                        onValueChange: setSearchString,
                        onKeyPress: (e)=>e.key == 'Enter' ? null : null
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(PPrintTree, {
                data: data,
                autoExpanded: __expanded__,
                fullExpand: fullExpand,
                searchString: searchString
            })
        ]
    });
});
export const LoremIpsum = ({ partcfg })=>/*#__PURE__*/ _jsx(LoremBuilder, {
        small: partcfg.small === true
    });
